// Need to use the React-specific entry point to import createApi
import config from "../config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import {
  Answer,
  AnswerQuestionOption,
  QuestionGroupWithRelations,
} from "../../../api/src/types";
import {
  createQuestionnaireSectionDetailsTagId,
  createQuestionnaireSectionsTagId,
  questionnaireApi,
} from "./questionnaire";
import { RootState } from "../store";
import {
  createSerializedFetchBaseQuery,
  withHeader,
  SERIALIZE_BY_HEADER,
} from "./serializedFetchBaseQuery";
import {
  computeAnswerUpsertPlan,
  dedupePreserveOrder,
  normalizeValueTyped,
} from "../../../api/src/users/answers/upsert-rules";
import type {
  AnswersDisputeGetResponse,
  AnswersWithAttorneyRestrictionsGetResponse,
} from "../../../api/src/users/answers/route";
import { ResolutionsGetResponse } from "../../../api/src/users/answers/resolutions/route";
import { documentsApi } from "./documents";

type AnswerWithRelations = Answer & {
  answerQuestionOptions: AnswerQuestionOption[];
};

// after a failure, we null out local state and for this question and its children, so on the next update
// we will use this flag to tell the backend to clear down the tree so we don't revive old db state
const cleanSlateOnNextUpdate = new Set<string>();

const createCleanSlateKey = (params: {
  userId: string;
  documentId: string;
  questionId: string;
}): string => {
  return `answers-clean-slate:${params.userId}:${params.documentId}:${params.questionId}`;
};

interface SectionGroupsArgs {
  docId: string;
  section: string;
  userId: string;
}

function targetInCache(
  dispatch: (action: unknown) => unknown,
  state: RootState,
  args: SectionGroupsArgs,
  updater: (draft: QuestionGroupWithRelations[]) => void,
): void {
  const queryState =
    questionnaireApi.endpoints.getQuestionnaireSectionGroupsByDocumentId.select(
      args,
    )(state);
  if (!queryState.data) return;

  dispatch(
    questionnaireApi.util.updateQueryData(
      "getQuestionnaireSectionGroupsByDocumentId",
      args,
      updater,
    ),
  );
}

function updateInCache(params: {
  draft: QuestionGroupWithRelations[];
  questionId: string;
  userId: string;
  documentId: string;
  valueTyped: string | null | undefined;
  questionOptionIds: string[] | undefined;
  forceCleanSlate: boolean;
}) {
  const {
    draft,
    questionId,
    userId,
    documentId,
    valueTyped,
    questionOptionIds,
    forceCleanSlate,
  } = params;

  const question = draft
    .flatMap((g) => g.questions)
    .find((q) => q.id === questionId);
  if (!question) return;

  const existingAnswer = question.answers[0] as AnswerWithRelations | undefined;
  const currentOptionIds =
    existingAnswer?.answerQuestionOptions.map((o) => o.questionOptionId) ?? [];

  const plan = computeAnswerUpsertPlan({
    questionType: question.questionType,
    hasExistingAnswer: Boolean(existingAnswer),
    currentOptionIds,
    nextValueTyped: valueTyped,
    nextOptionIds: questionOptionIds,
    forceCleanSlate,
  });

  switch (plan.kind) {
    case "no-op": {
      question.answers = [];
      break;
    }
    case "create": {
      const normalizedOptionIds = dedupePreserveOrder(questionOptionIds ?? []);
      const { normalizedValueTyped } = normalizeValueTyped(valueTyped);

      question.answers = [
        createOptimisticAnswer(
          questionId,
          userId,
          normalizedValueTyped,
          normalizedOptionIds,
          documentId,
        ),
      ];
      break;
    }
    case "delete": {
      question.answers = [];
      break;
    }
    case "update": {
      if (existingAnswer) {
        const now = new Date();
        if (plan.valueTypedUpdate.kind === "set") {
          existingAnswer.valueTyped = plan.valueTypedUpdate.value;
        }
        existingAnswer.updatedAt = now;
        existingAnswer.answerQuestionOptions = plan.normalizedOptionIds.map(
          (optionId) => ({
            questionOptionId: optionId,
            userId,
            answerId: existingAnswer.id,
            isDefault: false,
            createdAt: now,
            updatedAt: now,
          }),
        );
      } else {
        // plan.kind === "update" implies `existingAnswer` existed when plan was computed.
        // If cache is unexpectedly out of sync, do nothing rather than creating a phantom optimistic answer.
      }
      break;
    }
  }

  if (plan.willDeleteChildren) {
    clearDescendantAnswers({ draft, parentQuestionId: questionId });
  }
}

const clearDescendantAnswers = (params: {
  draft: QuestionGroupWithRelations[];
  parentQuestionId: string;
}): number => {
  const questions = params.draft.flatMap((g) => g.questions);
  const questionById = new Map(questions.map((q) => [q.id, q] as const));
  const childrenByParentId = new Map<string, string[]>();

  questions.forEach((q) => {
    if (!q.parentQuestionId) return;
    const list = childrenByParentId.get(q.parentQuestionId) ?? [];
    list.push(q.id);
    childrenByParentId.set(q.parentQuestionId, list);
  });

  const stack: string[] = childrenByParentId.get(params.parentQuestionId)
    ? [...(childrenByParentId.get(params.parentQuestionId) ?? [])]
    : [];

  let clearedCount = 0;
  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) continue;

    const question = questionById.get(currentId);
    if (question) {
      question.answers = [];
      clearedCount += 1;
    }

    const children = childrenByParentId.get(currentId) ?? [];
    children.forEach((childId) => stack.push(childId));
  }

  return clearedCount;
};

export const answersApi = createApi({
  reducerPath: "answersApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["answers", "documents", "disputes", "resolutions"],
  endpoints: (builder) => ({
    getAnswer: builder.query<
      Answer[],
      { questionId?: string; userId: string; documentId: string }
    >({
      query: ({ questionId, userId, documentId }) => {
        const searchParams = new URLSearchParams({ documentId });
        if (questionId) {
          searchParams.set("questionId", questionId);
        }
        return {
          url: `/users/${userId}/answers?${searchParams.toString()}`,
        };
      },
      providesTags: ["answers"],
    }),
    upsertAnswer: builder.mutation<
      Answer | null,
      {
        questionId: string;
        userId: string;
        documentId: string;
        rootQuestionId: string;
        sectionId: string;
        serializeDebounceMs?: number;
        valueTyped?: string | null;
        questionOptionIds?: string[];
      }
    >({
      query: ({
        questionId,
        userId,
        documentId,
        serializeDebounceMs,
        valueTyped,
        questionOptionIds,
      }) => ({
        url: `/users/${userId}/answers?questionId=${questionId}`,
        method: "POST",
        body: {
          valueTyped: valueTyped ?? null,
          questionOptionIds: questionOptionIds ?? null,
          forceCleanSlate: (() => {
            const key = createCleanSlateKey({
              userId,
              documentId,
              questionId,
            });
            return cleanSlateOnNextUpdate.has(key);
          })(),
        },
        headers: (() => {
          let headers = withHeader(undefined, SERIALIZE_BY_HEADER, documentId);
          headers = withHeader(headers, "X-Prune-By", `${questionId}-POST`);
          if (serializeDebounceMs != null) {
            headers = withHeader(
              headers,
              "X-Serialize-Debounce-Ms",
              String(serializeDebounceMs),
            );
          }
          return headers;
        })(),
      }),
      async onQueryStarted(
        {
          questionId,
          userId,
          documentId,
          valueTyped,
          questionOptionIds,
          sectionId,
        },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { dispatch, queryFulfilled, getState },
      ) {
        const sectionGroupsArgs = {
          docId: documentId,
          section: sectionId,
          userId,
        } as const;

        const cleanSlateKey = createCleanSlateKey({
          userId,
          documentId,
          questionId,
        });
        const forceCleanSlate = cleanSlateOnNextUpdate.has(cleanSlateKey);

        const state = getState() as RootState;
        targetInCache(dispatch, state, sectionGroupsArgs, (draft) => {
          updateInCache({
            draft,
            questionId,
            userId,
            documentId,
            valueTyped,
            questionOptionIds,
            forceCleanSlate,
          });
        });

        try {
          await queryFulfilled;
          if (forceCleanSlate) {
            cleanSlateOnNextUpdate.delete(cleanSlateKey);
          }
        } catch (error) {
          const state = getState() as RootState;
          targetInCache(dispatch, state, sectionGroupsArgs, (draft) => {
            clearInCache({
              draft,
              parentQuestionId: questionId,
            });
          });
          cleanSlateOnNextUpdate.add(cleanSlateKey);
          console.error("Failed to upsert answer:", error);
        } finally {
          const questionnaireTags = [
            {
              type: "QuestionnaireSections" as const,
              id: createQuestionnaireSectionDetailsTagId({
                userId,
                section: sectionId,
              }),
            },
            {
              type: "QuestionnaireSections" as const,
              id: createQuestionnaireSectionsTagId({
                userId,
                includeStatus: true,
              }),
            },
            { type: "QuestionOptionVisibility" as const },
          ];
          dispatch(questionnaireApi.util.invalidateTags(questionnaireTags));
          dispatch(
            documentsApi.util.invalidateTags([
              "documents",
              "regeneration-status",
            ]),
          );
        }
      },
      invalidatesTags: ["answers"],
    }),
    getAnswersWithAttorneyRestrictions: builder.query<
      AnswersWithAttorneyRestrictionsGetResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/${userId}/answers/attorney-restrictions`,
      }),
      providesTags: ["answers"],
    }),
    getDisputes: builder.query<AnswersDisputeGetResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/answers/dispute`,
      }),
      providesTags: ["disputes"],
    }),
    getResolutions: builder.query<
      ResolutionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/documents/${documentId}/resolutions`,
      }),
      providesTags: ["resolutions"],
    }),
  }),
});

export const {
  useGetAnswerQuery,
  useUpsertAnswerMutation,
  useGetAnswersWithAttorneyRestrictionsQuery,
  useGetDisputesQuery,
  useGetResolutionsQuery,
} = answersApi;

function clearInCache(params: {
  draft: QuestionGroupWithRelations[];
  parentQuestionId: string;
}): void {
  const { draft, parentQuestionId } = params;

  const question = draft
    .flatMap((g) => g.questions)
    .find((q) => q.id === parentQuestionId);
  if (!question) return;

  question.answers = [];
  clearDescendantAnswers({ draft, parentQuestionId });
}

// Helper to create optimistic answer data
const createOptimisticAnswer = (
  questionId: string,
  userId: string,
  valueTyped: string | null | undefined,
  questionOptionIds: string[] | undefined,
  documentId = "",
): AnswerWithRelations => {
  const now = new Date();
  const tempId = `temp-${now.getTime()}`;

  return {
    id: tempId,
    questionId,
    userId,
    documentId,
    valueTyped: valueTyped ?? null,
    deletedAt: null,
    createdAt: now,
    updatedAt: now,
    createdById: userId,
    updatedById: userId,
    answerQuestionOptions:
      questionOptionIds?.map((optionId) => ({
        questionOptionId: optionId,
        answerId: tempId,
        isDefault: false,
        createdAt: now,
        updatedAt: now,
      })) ?? [],
  };
};
